// Firebase Firestore database service
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase.js';

class FirebaseDatabaseService {
  constructor() {
    this.playersCollection = 'players';
  }

  /**
   * Get player data from Firestore
   * @param {string} userId - User's UID
   * @returns {Promise<Object>} Player data or error
   */
  async getPlayerData(userId) {
    try {
      const playerDocRef = doc(db, this.playersCollection, userId);
      const playerDoc = await getDoc(playerDocRef);
      
      if (playerDoc.exists()) {
        const data = playerDoc.data();
        // Ensure language-specific fields exist
        return {
          success: true,
          data: {
            ...data,
            // Level status tracking (three-state system: won, failed, not_done)
            levelStatus_en: data.levelStatus_en || {},
            levelStatus_da: data.levelStatus_da || {},
          }
        };
      } else {
        // Return default player data structure with language-specific fields
        return {
          success: true,
          data: {
            playername: '',
            metersSkatedRecord: 0,
            totalMeters: 0,
            totalScore: 0,
            bestGameScore: 0,
            topWords: [],
            // Level status tracking (three-state system: won, failed, not_done)
            levelStatus_en: {},
            levelStatus_da: {}
          }
        };
      }
    } catch (error) {
      console.error('Error getting player data:', error);
      return {
        success: false,
        error: 'Failed to load player data. Please try again.'
      };
    }
  }

  /**
   * Create or update player document
   * @param {string} userId - User's UID
   * @param {Object} playerData - Player data to save
   * @returns {Promise<Object>} Success status or error
   */
  async createOrUpdatePlayer(userId, playerData) {
    try {
      const playerDocRef = doc(db, this.playersCollection, userId);
      await setDoc(playerDocRef, {
        ...playerData,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error creating/updating player:', error);
      return {
        success: false,
        error: 'Failed to save player data. Please try again.'
      };
    }
  }

  /**
   * Update player's meters skated record if new record is higher
   * @param {string} userId - User's UID
   * @param {number} metersSkated - Meters skated in current game
   * @returns {Promise<Object>} Success status and whether it was a new record
   */
  async updateMetersRecord(userId, metersSkated) {
    try {
      const playerData = await this.getPlayerData(userId);
      if (!playerData.success) {
        return playerData;
      }

      const currentRecord = playerData.data.metersSkatedRecord || 0;
      const isNewRecord = metersSkated > currentRecord;

      if (isNewRecord) {
        const playerDocRef = doc(db, this.playersCollection, userId);
        await updateDoc(playerDocRef, {
          metersSkatedRecord: metersSkated,
          updatedAt: serverTimestamp()
        });

        return {
          success: true,
          isNewRecord: true,
          previousRecord: currentRecord,
          newRecord: metersSkated
        };
      }

      return {
        success: true,
        isNewRecord: false,
        currentRecord: currentRecord
      };
    } catch (error) {
      console.error('Error updating meters record:', error);
      return {
        success: false,
        error: 'Failed to update meters record. Please try again.'
      };
    }
  }

  /**
   * Add a word to player's top words list (maintains top 10)
   * @param {string} userId - User's UID
   * @param {string} word - The word (uppercase)
   * @param {number} points - Points scored for the word
   * @returns {Promise<Object>} Success status and whether it made the top 10
   */
  async addTopWord(userId, word, points) {
    try {
      const playerData = await this.getPlayerData(userId);
      if (!playerData.success) {
        return playerData;
      }

      const currentTopWords = playerData.data.topWords || [];
      const newWord = {
        word: word.toUpperCase(),
        points: points,
        timestamp: new Date()
      };

      // Check if this word qualifies for top 10
      const sortedWords = [...currentTopWords, newWord]
        .sort((a, b) => b.points - a.points)
        .slice(0, 10); // Keep only top 10

      const madeTopTen = sortedWords.some(w => 
        w.word === newWord.word && 
        w.points === newWord.points && 
        Math.abs(w.timestamp.getTime() - newWord.timestamp.getTime()) < 1000
      );

      if (madeTopTen) {
        const playerDocRef = doc(db, this.playersCollection, userId);

        // Create a map of existing words with their original timestamps
        const existingWordsMap = new Map(
          currentTopWords.map(word => [`${word.word}-${word.points}`, word.timestamp])
        );

        await updateDoc(playerDocRef, {
          topWords: sortedWords.map(w => {
            const wordKey = `${w.word}-${w.points}`;
            const existingTimestamp = existingWordsMap.get(wordKey);

            if (existingTimestamp) {
              console.log(`🔥 addTopWord: Preserving timestamp for existing word: ${w.word} (${w.points}pts) - ${existingTimestamp}`);
            } else {
              console.log(`🔥 addTopWord: Assigning new timestamp for new word: ${w.word} (${w.points}pts)`);
            }

            return {
              ...w,
              // Preserve original timestamp if word existed, otherwise use current timestamp
              timestamp: existingTimestamp || new Date()
            };
          }),
          updatedAt: serverTimestamp()
        });

        return {
          success: true,
          madeTopTen: true,
          rank: sortedWords.findIndex(w => 
            w.word === newWord.word && w.points === newWord.points
          ) + 1
        };
      }

      return {
        success: true,
        madeTopTen: false,
        minimumPoints: currentTopWords.length >= 10 ? 
          Math.min(...currentTopWords.map(w => w.points)) : 0
      };
    } catch (error) {
      console.error('Error adding top word:', error);
      return {
        success: false,
        error: 'Failed to save word score. Please try again.'
      };
    }
  }

  /**
   * Update player's display name
   * @param {string} userId - User's UID
   * @param {string} playername - New display name
   * @returns {Promise<Object>} Success status or error
   */
  async updatePlayerName(userId, playername) {
    try {
      const playerDocRef = doc(db, this.playersCollection, userId);
      await updateDoc(playerDocRef, {
        playername: playername,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating player name:', error);
      return {
        success: false,
        error: 'Failed to update player name. Please try again.'
      };
    }
  }

  /**
   * Batch update player data at game over (atomic operation)
   * @param {string} userId - User's UID
   * @param {Object} updates - Object containing fields to update
   * @param {number} [updates.metersSkatedRecord] - New meters record (if higher than current)
   * @param {number} [updates.totalMeters] - New total cumulative meters (added to current)
   * @param {number} [updates.totalScore] - New total cumulative score (added to current)
   * @param {number} [updates.bestGameScore] - New best single-game score (if higher than current)
   * @param {Array} [updates.topWords] - Updated top words array
   * @returns {Promise<Object>} Success status and update details
   */
  async batchUpdatePlayerData(userId, updates) {
    console.log('🔥 batchUpdatePlayerData() called');
    console.log('🔥 User ID:', userId);
    console.log('🔥 Updates to apply:', JSON.stringify(updates, null, 2));

    try {
      // First get current player data to validate updates
      console.log('🔥 Getting current player data...');
      const playerData = await this.getPlayerData(userId);
      console.log('🔥 Current player data result:', playerData);

      if (!playerData.success) {
        console.error('🔥 Failed to get current player data:', playerData);
        return playerData;
      }

      const currentData = playerData.data;
      console.log('🔥 Current player data:', JSON.stringify(currentData, null, 2));

      const batch = writeBatch(db);
      const playerDocRef = doc(db, this.playersCollection, userId);

      const batchUpdates = {
        updatedAt: serverTimestamp()
      };

      let hasUpdates = false;
      const updateResults = {
        metersRecord: { updated: false },
        totalMeters: { updated: false },
        totalScore: { updated: false },
        bestGameScore: { updated: false },
        topWords: { updated: false }
      };

      console.log('🔥 Processing updates...');

      // Handle meters record update
      if (updates.metersSkatedRecord !== undefined) {
        console.log('🔥 Processing meters record update...');
        const currentRecord = currentData.metersSkatedRecord || 0;
        const newRecord = updates.metersSkatedRecord;
        console.log('🔥 Meters comparison:', { currentRecord, newRecord });

        if (newRecord > currentRecord) {
          console.log('🔥 New meters record! Adding to batch update');
          batchUpdates.metersSkatedRecord = newRecord;
          hasUpdates = true;
          updateResults.metersRecord = {
            updated: true,
            previousRecord: currentRecord,
            newRecord: newRecord
          };
        } else {
          console.log('🔥 No new meters record (current >= new)');
        }
      }

      // Handle total meters update
      if (updates.totalMeters !== undefined) {
        console.log('🔥 Processing total meters update...');
        const currentTotalMeters = currentData.totalMeters || 0;
        const gameMeters = updates.totalMeters;
        const newTotalMeters = currentTotalMeters + gameMeters;
        console.log('🔥 Meters calculation:', { currentTotalMeters, gameMeters, newTotalMeters });

        if (gameMeters > 0) {
          console.log('🔥 Adding game meters to total! Adding to batch update');
          batchUpdates.totalMeters = newTotalMeters;
          hasUpdates = true;
          updateResults.totalMeters = {
            updated: true,
            previousTotal: currentTotalMeters,
            gameMeters: gameMeters,
            newTotal: newTotalMeters
          };
        } else {
          console.log('🔥 No meters to add (game meters <= 0)');
        }
      }

      // Handle total score update
      if (updates.totalScore !== undefined) {
        console.log('🔥 Processing total score update...');
        const currentTotalScore = currentData.totalScore || 0;
        const gameScore = updates.totalScore;
        const newTotalScore = currentTotalScore + gameScore;
        console.log('🔥 Score calculation:', { currentTotalScore, gameScore, newTotalScore });

        if (gameScore > 0) {
          console.log('🔥 Adding game score to total! Adding to batch update');
          batchUpdates.totalScore = newTotalScore;
          hasUpdates = true;
          updateResults.totalScore = {
            updated: true,
            previousTotal: currentTotalScore,
            gameScore: gameScore,
            newTotal: newTotalScore
          };
        } else {
          console.log('🔥 No score to add (game score <= 0)');
        }
      }

      // Handle best game score update
      if (updates.bestGameScore !== undefined) {
        console.log('🔥 Processing best game score update...');
        const currentBestScore = currentData.bestGameScore || 0;
        const newGameScore = updates.bestGameScore;
        console.log('🔥 Best score comparison:', { currentBestScore, newGameScore });

        if (newGameScore > currentBestScore) {
          console.log('🔥 New best game score! Adding to batch update');
          batchUpdates.bestGameScore = newGameScore;
          hasUpdates = true;
          updateResults.bestGameScore = {
            updated: true,
            previousBest: currentBestScore,
            newBest: newGameScore
          };
        } else {
          console.log('🔥 No new best game score (current >= new)');
        }
      }

      // Handle top words update
      if (updates.topWords !== undefined) {
        console.log('🔥 Processing top words update...');
        const currentTopWords = currentData.topWords || [];
        const newTopWords = updates.topWords;
        console.log('🔥 Top words comparison:', {
          currentCount: currentTopWords.length,
          newCount: newTopWords.length,
          currentTopWords: currentTopWords.slice(0, 3), // Show first 3 for debugging
          newTopWords: newTopWords.slice(0, 3)
        });

        // Check if top words actually changed
        const topWordsChanged = this._hasTopWordsChanged(currentTopWords, newTopWords);
        console.log('🔥 Top words changed?', topWordsChanged);

        if (topWordsChanged) {
          console.log('🔥 Top words changed! Adding to batch update');
          // FIXED: Preserve original timestamps for existing words, only update new words
          // Create a map of existing words with their original timestamps
          const existingWordsMap = new Map(
            currentTopWords.map(word => [`${word.word}-${word.points}`, word.timestamp])
          );

          const firestoreTopWords = newTopWords.map(word => {
            const wordKey = `${word.word}-${word.points}`;
            const existingTimestamp = existingWordsMap.get(wordKey);

            if (existingTimestamp) {
              console.log(`🔥 Preserving timestamp for existing word: ${word.word} (${word.points}pts) - ${existingTimestamp}`);
            } else {
              console.log(`🔥 Assigning new timestamp for new word: ${word.word} (${word.points}pts)`);
            }

            return {
              ...word,
              // Preserve original timestamp if word existed, otherwise use current timestamp
              timestamp: existingTimestamp || new Date()
            };
          });

          batchUpdates.topWords = firestoreTopWords;
          hasUpdates = true;
          updateResults.topWords = {
            updated: true,
            newWordsCount: this._countNewWords(currentTopWords, newTopWords)
          };
        } else {
          console.log('🔥 No top words changes detected');
        }
      }

      console.log('🔥 Final batch updates:', JSON.stringify(batchUpdates, null, 2));
      console.log('🔥 Has updates?', hasUpdates);

      // Only commit if there are actual updates
      if (hasUpdates) {
        console.log('🔥 Committing batch update to Firestore...');
        batch.update(playerDocRef, batchUpdates);
        await batch.commit();
        console.log('🔥 Batch update committed successfully!');
      } else {
        console.log('🔥 No updates to commit');
      }

      const finalResult = {
        success: true,
        hasUpdates,
        updateResults
      };

      console.log('🔥 Returning final result:', finalResult);
      return finalResult;

    } catch (error) {
      console.error('❌ Error batch updating player data:', error);
      console.error('❌ Error stack:', error.stack);
      return {
        success: false,
        error: 'Failed to save game progress. Please try again.'
      };
    }
  }

  /**
   * Helper method to check if top words array has changed
   * @private
   */
  _hasTopWordsChanged(currentTopWords, newTopWords) {
    if (currentTopWords.length !== newTopWords.length) return true;

    return newTopWords.some((word, index) => {
      const current = currentTopWords[index];
      return !current ||
             word.word !== current.word ||
             word.points !== current.points;
    });
  }

  /**
   * Helper method to count how many new words made it to top words
   * @private
   */
  _countNewWords(currentTopWords, newTopWords) {
    const currentWordSet = new Set(
      currentTopWords.map(w => `${w.word}-${w.points}`)
    );

    return newTopWords.filter(word =>
      !currentWordSet.has(`${word.word}-${word.points}`)
    ).length;
  }

  /**
   * Set the completion status of a level (won, failed, or not_done)
   * @param {string} userId - User's UID
   * @param {number} levelNumber - Level number (1-100)
   * @param {string} status - Level status: 'won', 'failed', or 'not_done' (null)
   * @param {string} language - Language code ('en' or 'da')
   * @returns {Promise<Object>} Success status
   */
  async setLevelStatus(userId, levelNumber, status, language = 'en') {
    try {
      // Validate status
      if (!['won', 'failed', 'not_done'].includes(status)) {
        return {
          success: false,
          error: 'Invalid status. Must be "won", "failed", or "not_done".'
        };
      }

      const playerData = await this.getPlayerData(userId);
      if (!playerData.success) {
        return playerData;
      }

      // Use field structure: levelStatus_en or levelStatus_da
      // This is an object mapping level numbers to status strings
      const statusFieldName = `levelStatus_${language}`;
      const levelStatuses = playerData.data[statusFieldName] || {};

      // Use String() to ensure consistent key format since Firebase stores keys as strings
      const levelKey = String(levelNumber);

      // Set or remove the status
      if (status === 'not_done') {
        // Remove the level from the status map (treat as not attempted)
        delete levelStatuses[levelKey];
      } else {
        // Set the status to 'won' or 'failed'
        levelStatuses[levelKey] = status;
      }

      const playerDocRef = doc(db, this.playersCollection, userId);
      await updateDoc(playerDocRef, {
        [statusFieldName]: levelStatuses,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error setting level status:', error);
      return {
        success: false,
        error: 'Failed to save level status. Please try again.'
      };
    }
  }

  /**
   * Get the status of a specific level
   * @param {string} userId - User's UID
   * @param {number} levelNumber - Level number (1-100)
   * @param {string} language - Language code ('en' or 'da')
   * @returns {Promise<Object>} Status ('won', 'failed', or 'not_done')
   */
  async getLevelStatus(userId, levelNumber, language = 'en') {
    try {
      const playerData = await this.getPlayerData(userId);
      if (!playerData.success) {
        return playerData;
      }

      const statusFieldName = `levelStatus_${language}`;
      const levelStatuses = playerData.data[statusFieldName] || {};
      // Use String() to ensure proper key access since Firebase stores keys as strings
      const status = levelStatuses[String(levelNumber)] || 'not_done';

      return {
        success: true,
        status
      };
    } catch (error) {
      console.error('Error getting level status:', error);
      return {
        success: false,
        error: 'Failed to load level status. Please try again.'
      };
    }
  }

  /**
   * Get current level for a specific language
   * Calculates dynamically from levelStatus as the next level after the highest reached level
   * (highest level that has been won OR failed)
   * @param {string} userId - User's UID
   * @param {string} language - Language code ('en' or 'da')
   * @returns {Promise<Object>} Current level number or error
   */
  async getCurrentLevel(userId, language = 'en') {
    try {
      const playerData = await this.getPlayerData(userId);
      if (!playerData.success) {
        return playerData;
      }

      // Calculate current level from levelStatus
      const statusFieldName = `levelStatus_${language}`;
      const levelStatuses = playerData.data[statusFieldName] || {};

      // Find the highest level that has been attempted (won or failed)
      // Use String() to ensure proper key access since Firebase stores keys as strings
      let highestReachedLevel = 0;
      for (let level = 1; level <= 100; level++) {
        const status = levelStatuses[String(level)];
        if (status === 'won' || status === 'failed') {
          highestReachedLevel = level;
        }
      }

      // Current level is the next level after the highest reached level
      const currentLevel = Math.min(highestReachedLevel + 1, 100);

      return {
        success: true,
        currentLevel
      };
    } catch (error) {
      console.error('Error getting current level:', error);
      return {
        success: false,
        error: 'Failed to load level progress. Please try again.'
      };
    }
  }
}

// Export a singleton instance
const firebaseDatabase = new FirebaseDatabaseService();
export default firebaseDatabase;
